/**
 * Board Panels Store - manages board files and notes sidebar panels
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { boardNotesApi, type BoardNote } from '@services/api/boardNotes';
import { boardFilesApi, type Attachment } from '@services/api/boardFiles';

interface BoardPanelsState {
  // Panel visibility (localStorage persisted)
  leftPanelOpen: boolean;
  rightPanelOpen: boolean;

  // Notes state
  notes: BoardNote[];
  selectedNoteId: string | null;
  isLoadingNotes: boolean;
  isSavingNote: boolean;
  notesError: string | null;

  // Files state
  files: Attachment[];
  isLoadingFiles: boolean;
  isUploading: boolean;
  uploadProgress: number;
  filesError: string | null;

  // Current board context
  currentBoardId: string | null;

  // Panel actions
  toggleLeftPanel: () => void;
  toggleRightPanel: () => void;
  setLeftPanelOpen: (open: boolean) => void;
  setRightPanelOpen: (open: boolean) => void;

  // Board context
  setBoardContext: (boardId: string) => void;
  clearBoardContext: () => void;

  // Notes actions
  fetchNotes: (boardId: string) => Promise<void>;
  createNote: (boardId: string, data: { title: string; content?: string }) => Promise<BoardNote>;
  updateNote: (
    boardId: string,
    noteId: string,
    data: { title?: string; content?: string }
  ) => Promise<BoardNote>;
  deleteNote: (boardId: string, noteId: string) => Promise<void>;
  reorderNotes: (boardId: string, noteIds: string[]) => Promise<void>;
  selectNote: (noteId: string | null) => void;

  // Files actions
  fetchFiles: (boardId: string) => Promise<void>;
  uploadFile: (boardId: string, file: File) => Promise<Attachment>;
  deleteFile: (attachmentId: string) => Promise<void>;
  refreshFileUrl: (attachmentId: string) => Promise<Attachment>;

  // Reset
  reset: () => void;
}

const initialState = {
  leftPanelOpen: false,
  rightPanelOpen: false,
  notes: [] as BoardNote[],
  selectedNoteId: null as string | null,
  isLoadingNotes: false,
  isSavingNote: false,
  notesError: null as string | null,
  files: [] as Attachment[],
  isLoadingFiles: false,
  isUploading: false,
  uploadProgress: 0,
  filesError: null as string | null,
  currentBoardId: null as string | null,
};

export const useBoardPanelsStore = create<BoardPanelsState>()(
  persist(
    immer((set, get) => ({
      ...initialState,

      // =========================================================================
      // Panel Actions
      // =========================================================================

      toggleLeftPanel: () => {
        set((state) => {
          state.leftPanelOpen = !state.leftPanelOpen;
        });
      },

      toggleRightPanel: () => {
        set((state) => {
          state.rightPanelOpen = !state.rightPanelOpen;
        });
      },

      setLeftPanelOpen: (open) => {
        set((state) => {
          state.leftPanelOpen = open;
        });
      },

      setRightPanelOpen: (open) => {
        set((state) => {
          state.rightPanelOpen = open;
        });
      },

      // =========================================================================
      // Board Context
      // =========================================================================

      setBoardContext: (boardId) => {
        const currentBoardId = get().currentBoardId;
        if (currentBoardId !== boardId) {
          set((state) => {
            state.currentBoardId = boardId;
            // Clear data when switching boards
            state.notes = [];
            state.files = [];
            state.selectedNoteId = null;
            state.notesError = null;
            state.filesError = null;
          });
        }
      },

      clearBoardContext: () => {
        set((state) => {
          state.currentBoardId = null;
          state.notes = [];
          state.files = [];
          state.selectedNoteId = null;
          state.notesError = null;
          state.filesError = null;
        });
      },

      // =========================================================================
      // Notes Actions
      // =========================================================================

      fetchNotes: async (boardId) => {
        set((state) => {
          state.isLoadingNotes = true;
          state.notesError = null;
        });

        try {
          const notes = await boardNotesApi.list(boardId);
          set((state) => {
            state.notes = notes;
            state.isLoadingNotes = false;
          });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to load notes';
          set((state) => {
            state.isLoadingNotes = false;
            state.notesError = message;
          });
        }
      },

      createNote: async (boardId, data) => {
        set((state) => {
          state.isSavingNote = true;
        });

        try {
          const note = await boardNotesApi.create(boardId, data);
          set((state) => {
            state.notes.push(note);
            state.isSavingNote = false;
            state.selectedNoteId = note.id;
          });
          return note;
        } catch (error) {
          set((state) => {
            state.isSavingNote = false;
          });
          throw error;
        }
      },

      updateNote: async (boardId, noteId, data) => {
        set((state) => {
          state.isSavingNote = true;
        });

        try {
          const updatedNote = await boardNotesApi.update(boardId, noteId, data);
          set((state) => {
            const idx = state.notes.findIndex((n) => n.id === noteId);
            if (idx !== -1) {
              state.notes[idx] = updatedNote;
            }
            state.isSavingNote = false;
          });
          return updatedNote;
        } catch (error) {
          set((state) => {
            state.isSavingNote = false;
          });
          throw error;
        }
      },

      deleteNote: async (boardId, noteId) => {
        await boardNotesApi.delete(boardId, noteId);
        set((state) => {
          state.notes = state.notes.filter((n) => n.id !== noteId);
          if (state.selectedNoteId === noteId) {
            state.selectedNoteId = null;
          }
        });
      },

      reorderNotes: async (boardId, noteIds) => {
        const reorderedNotes = await boardNotesApi.reorder(boardId, noteIds);
        set((state) => {
          state.notes = reorderedNotes;
        });
      },

      selectNote: (noteId) => {
        set((state) => {
          state.selectedNoteId = noteId;
        });
      },

      // =========================================================================
      // Files Actions
      // =========================================================================

      fetchFiles: async (boardId) => {
        set((state) => {
          state.isLoadingFiles = true;
          state.filesError = null;
        });

        try {
          const files = await boardFilesApi.list(boardId);
          set((state) => {
            state.files = files;
            state.isLoadingFiles = false;
          });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to load files';
          set((state) => {
            state.isLoadingFiles = false;
            state.filesError = message;
          });
        }
      },

      uploadFile: async (boardId, file) => {
        set((state) => {
          state.isUploading = true;
          state.uploadProgress = 0;
        });

        try {
          // Step 1: Initiate upload
          const { attachmentId, uploadUrl } = await boardFilesApi.initiateUpload(boardId, {
            filename: file.name,
            contentType: file.type || 'application/octet-stream',
            sizeBytes: file.size,
          });

          set((state) => {
            state.uploadProgress = 30;
          });

          // Step 2: Upload file to storage
          await boardFilesApi.uploadFile(uploadUrl, file);

          set((state) => {
            state.uploadProgress = 70;
          });

          // Step 3: Confirm upload
          const attachment = await boardFilesApi.confirmUpload(attachmentId);

          set((state) => {
            state.files.push(attachment);
            state.isUploading = false;
            state.uploadProgress = 100;
          });

          // Reset progress after a short delay
          setTimeout(() => {
            set((state) => {
              state.uploadProgress = 0;
            });
          }, 500);

          return attachment;
        } catch (error) {
          set((state) => {
            state.isUploading = false;
            state.uploadProgress = 0;
          });
          throw error;
        }
      },

      deleteFile: async (attachmentId) => {
        await boardFilesApi.delete(attachmentId);
        set((state) => {
          state.files = state.files.filter((f) => f.id !== attachmentId);
        });
      },

      refreshFileUrl: async (attachmentId) => {
        const refreshedAttachment = await boardFilesApi.getAttachment(attachmentId);
        set((state) => {
          const idx = state.files.findIndex((f) => f.id === attachmentId);
          if (idx !== -1) {
            state.files[idx] = refreshedAttachment;
          }
        });
        return refreshedAttachment;
      },

      // =========================================================================
      // Reset
      // =========================================================================

      reset: () => {
        set(initialState);
      },
    })),
    {
      name: 'neokanban-board-panels',
      storage: createJSONStorage(() => localStorage),
      // Only persist panel visibility preferences
      partialize: (state) => ({
        leftPanelOpen: state.leftPanelOpen,
        rightPanelOpen: state.rightPanelOpen,
      }),
    }
  )
);

export default useBoardPanelsStore;
